import { useMutation, useQueryClient } from 'react-query';
import PostApi from 'api/post';
import {
  useInvalidatePostDetailQueries,
  useInvalidatePostsQueries,
} from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import { queryKey } from 'constants/queryKey';
import { useToast } from 'app/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { popupConfirm } from 'utils/popup';
import i18n from 'app/utils/i18n';
import { ApiClientError } from 'api/client';
import { PostDetail } from 'types/PostDetail';

export const useUpdatePostStatus = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidatePosts = useInvalidatePostsQueries();
  const invalidatePostDetail = useInvalidatePostDetailQueries();

  const updateStatusMutation = useMutation(
    ({
      id,
      status,
      shouldUpdateComments,
      confirmRepublish,
    }: {
      id: number;
      status: number;
      shouldUpdateComments?: boolean;
      confirmRepublish?: boolean;
    }) =>
      PostApi.savePartial({
        postId: id,
        values: { status, shouldUpdateComments, confirmRepublish },
      }),
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries(queryKey.postDetail(variables.id));

        const previousStatus = queryClient.getQueryData<PostDetail | null>(
          queryKey.postDetail(variables.id),
        )?.status;

        queryClient.setQueryData<PostDetail | null>(
          queryKey.postDetail(variables.id),
          (prevData) => {
            return prevData
              ? ({
                  ...prevData,
                  status: variables.status,
                  scheduling_information: null,
                } as PostDetail)
              : null;
          },
        );

        return { previousStatus };
      },
      onSuccess: () => {
        toast(t('statusUpdated'));
      },
      onSettled: (data, error, { id }) => {
        invalidatePosts();
        invalidatePostDetail({ postId: id });

        queryClient.invalidateQueries(queryKey.postActivityLog(id));
      },
      onError(e: ApiClientError, { id }, context) {
        if (context?.previousStatus !== undefined) {
          queryClient.setQueryData<PostDetail | null>(
            queryKey.postDetail(id),
            (prevData) => {
              return prevData
                ? ({
                    ...prevData,
                    status: context.previousStatus,
                  } as PostDetail)
                : null;
            },
          );
        }

        invalidatePostDetail({
          postId: id,
        });
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  /**
   * Moving a post that is already live onto a scheduler-backed status publishes it again,
   * which the scheduler's duplicate guard blocks unless the user confirms.
   */
  const updateStatus = async (
    id: number,
    status: number,
    additionalData?: {
      shouldUpdateComments?: boolean;
      wasPublished?: boolean;
      pageName?: string;
      onSettled?: () => void;
    },
  ) => {
    const { onSettled, wasPublished, pageName, ...values } =
      additionalData ?? {};

    const republishes =
      !!wasPublished &&
      (status === STATUS.SCHEDULED || status === STATUS.EXPORTED);

    if (republishes) {
      const { isConfirmed } = await popupConfirm({
        title: i18n.t('postAlreadyPublished', { pageName }),
        text: i18n.t('postAlreadyPublishedWarning', { pageName }),
        confirmButtonText: i18n.t('publishAgain'),
        customClass: { popup: '!tw-w-[400px]' },
      });

      if (!isConfirmed) {
        return;
      }
    }

    updateStatusMutation.mutate(
      { id, status, ...values, confirmRepublish: republishes },
      { onSettled },
    );
  };

  return Object.assign(updateStatus, {
    isLoading: updateStatusMutation.isLoading,
    pendingStatus: updateStatusMutation.variables?.status,
  });
};
